import React from 'react';
import { Box, Typography } from '@mui/material';
import { Copy as CopyIcon } from '@x5-react-uikit/icons';
import { Tooltip } from '@x5-react-uikit/core';

import { ClientList } from 'types/clientList';
import { ClientListStatus } from 'types/api/clientList';
import { CustomIcon, TableDotIcon } from 'components';
import { formatDateTZ } from 'utils/date';
import { ForRole } from 'containers';
import { WarningStatus } from 'widgets';

import { STATUS } from './dictionary';
import DownloadToolbar from '../DownloadToolbar';
import { Role } from '../../../../types/api/user/dictionaries';

interface Props {
  onRowClick?: (id: string, payload?) => void;
  onUploadClick: (id: string, payload?) => void;
  items: ClientList[];
}

const TableRowsGroup: React.FC<Props> = (props) => {
  const { onUploadClick, items } = props;

  const copyToClipboard = (text) => () => {
    if (navigator?.clipboard) void navigator.clipboard.writeText(text);
  };

  return (
    <tbody>
      {items.map((item) => {
        const status = STATUS[item.status];
        const { original_file, matched_file, markup_file } = item;

        return (
          <Box key={item.id} component="tr">
            <td>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Tooltip placement="top" content={status?.tooltip}>
                  <TableDotIcon {...status?.indicator} ml={2} />
                </Tooltip>
              </Box>
            </td>

            <td>
              <Box display="flex" alignItems="center" columnGap={4}>
                <Tooltip placement="top" content={item?.id}>
                  <Typography
                    component="p"
                    variant="h5"
                    color="grey.100"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {item?.id}
                  </Typography>
                </Tooltip>
                {item?.id && (
                  <Tooltip placement="top" content="скопировать ID списка">
                    <Box flex="0" color="grey.60">
                      <CopyIcon
                        onClick={copyToClipboard(item.id)}
                        width="14px"
                        height="14px"
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </td>

            <td>
              <Box display="flex" alignItems="center" columnGap={4}>
                <CustomIcon name="fileCsv" width="28" height="28" />
                <Typography
                  component="p"
                  variant="p2"
                  color="grey.100"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  <Box component="span" color="grey.80">
                    {original_file.name}
                  </Box>
                </Typography>
              </Box>
            </td>

            <td>
              <Typography variant="p2" color="grey.50">
                {formatDateTZ(item?.created_at, 'DD.MM.YYYY / HH:mm') || '-'}
              </Typography>
            </td>

            <td>{item?.numberOfDuplicates || '-'}</td>

            <td>
              <Box display="flex" alignItems="center" columnGap={4}>
                <CustomIcon
                  name="fileCsv"
                  width="28"
                  height="28"
                  sx={{ ...(!markup_file?.name && { opacity: '0.3' }) }}
                />
                <Typography
                  component="p"
                  variant="p2"
                  color="grey.100"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {markup_file?.name || '-'}
                </Typography>
              </Box>
            </td>

            <td>
              <ForRole role={Role.Partner}>
                <DownloadToolbar
                  clientId={item?.id}
                  fileId={markup_file?.id}
                  labelDownload="Скачать размеченный файл"
                  showUpload={false}
                  showDownload={Boolean(markup_file?.id)}
                />
              </ForRole>
              <ForRole role={Role.Moderator}>
                <DownloadToolbar
                  clientId={item?.id}
                  fileId={matched_file?.id}
                  labelDownload="Скачать смэтченный файл"
                  labelUpload="Загрузить размеченный файл"
                  onUploadClick={onUploadClick}
                  showUpload={Boolean(matched_file?.id)}
                  showDownload={Boolean(matched_file?.id)}
                />
              </ForRole>
            </td>

            <td>
              <Box display="flex" columnGap={2}>
                {item.status === ClientListStatus.ERROR && (
                  <WarningStatus scope="clientList" status={item.status} />
                )}
              </Box>
            </td>
          </Box>
        );
      })}
    </tbody>
  );
};

export default React.memo(TableRowsGroup);
